import { useEffect } from 'react'

import { Form, useFormikContext } from 'formik'

import { Alert, Box, Flex } from 'app/components/design-system-components'
import {
  getPropertyArrangementValue,
  shouldHideRefereeDetailsSection,
  shouldHideRentalLedger,
  shouldShowOtherTenantsAndLedger,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import { AddressSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/AddressSection'
import { ArrangementSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/ArrangementSection'
import { DateSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/DateSection'
import { DocumentSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/DocumentSection'
import { FormActions } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/FormActions'
import { RefereeSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/RefereeSection'
import { TenantsSection } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/TenantsSection'
import type { ApplicationApplicant } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/TenantsSection'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import { isRentalLedgerRequiredForDraftAddressByPolicy } from 'app/sm/apply/rental-ledger-required-policy.helpers'

// Simplified props interface following pm-messages pattern
interface FormBodyProps {
  isLoading?: boolean
  isDocumentLoading?: boolean
  removeLedgerAttachment?: (guidID: string) => void
  uploadLedgerAttachment?: (documentType: string) => void
  isRentalLedgerRequiredByAgencyPolicy?: boolean
  hideSubmitButton?: boolean
  isEditMode: boolean
  applicants?: ApplicationApplicant[] // Add applicants prop for auto-fill
  isQLDForm22?: boolean // Add isQLDForm22 prop
  rentalHistoryForPolicy?: Record<string, unknown>[]
  editingRentalHistoryGUID?: string | undefined
  onStatusChange?: (isLoading: boolean, canSubmit: boolean) => void
  onSubmitReady?: (submitHandler: () => void) => void
}

/**
 * Main form body component with simplified Formik integration
 * Following pm-messages pattern - uses useFormikContext() directly
 * No custom hook dependencies, just pure Formik
 */
export const FormBody = (props: FormBodyProps) => {
  const {
    isLoading = false,
    isDocumentLoading = false,
    removeLedgerAttachment,
    uploadLedgerAttachment,
    isRentalLedgerRequiredByAgencyPolicy = false,
    hideSubmitButton = false,
    isEditMode = false,
    applicants = [],
    isQLDForm22 = false,
    rentalHistoryForPolicy = [],
    editingRentalHistoryGUID,
    onStatusChange,
    onSubmitReady,
  } = props

  // Use Formik context directly - following pm-messages pattern
  const formikContext = useFormikContext<RentalHistoryFormValues>()
  const { values, isSubmitting, isValid, dirty, submitForm } = formikContext

  // Simple loading and submit state - no custom hook needed
  const formIsLoading = isLoading || isSubmitting
  const canSubmit = isValid && dirty && !formIsLoading

  // Notify parent of form status changes for reactive UI
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(formIsLoading, canSubmit)
    }
  }, [onStatusChange, formIsLoading, canSubmit])

  // Provide submit handler to parent component
  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady(submitForm)
    }
  }, [onSubmitReady, submitForm])

  // Simple submit handler for FormActions
  const handleSubmitForm = () => {
    submitForm()
  }

  // Conditional logic based on property arrangement
  const propertyArrangementValue =
    getPropertyArrangementValue(values.propertyArrangement)?.toString() || ''

  const hideRefereeDetails = shouldHideRefereeDetailsSection(
    propertyArrangementValue,
  )
  const showTenantsAndLedger = shouldShowOtherTenantsAndLedger(
    propertyArrangementValue,
  )
  const hideRentalLedger = shouldHideRentalLedger(propertyArrangementValue)
  const isRentalLedgerRequiredForAddressByPolicy =
    isRentalLedgerRequiredByAgencyPolicy &&
    isRentalLedgerRequiredForDraftAddressByPolicy({
      rentalHistory: rentalHistoryForPolicy,
      editingRentalHistoryGUID,
      draftRentalHistory: {
        guidID: editingRentalHistoryGUID,
        propertyArrangement: propertyArrangementValue,
        isCurrentAddress: values.isCurrentAddress,
        startDate: values.startDate,
        endDate: values.endDate,
      },
    })

  return (
    <Box mx="auto" maxWidth="720px">
      {!isEditMode && (
        <Alert mt={4} variant="blueWithBg" leftIconVariant="info" showLeftIcon>
          Send a rental reference once address history is added.
        </Alert>
      )}

      <Form>
        <Flex flexDirection="column" gap={6} mt={5}>
          {/* Address Information */}
          <AddressSection values={values} />

          {/* Property Arrangement */}
          <ArrangementSection values={values} />

          {/* Date Information */}
          <DateSection values={values} />

          {/* Joint Tenancy - conditionally rendered based on property arrangement */}
          {showTenantsAndLedger && !isQLDForm22 && (
            <TenantsSection values={values} applicants={applicants} />
          )}

          {/* Document Upload - conditionally rendered */}
          {!hideRentalLedger &&
            (removeLedgerAttachment || uploadLedgerAttachment) && (
              <DocumentSection
                values={values}
                removeLedgerAttachment={removeLedgerAttachment || (() => {})}
                uploadLedgerAttachment={uploadLedgerAttachment || (() => {})}
                isRentalLedgerRequiredByAgencyPolicy={
                  isRentalLedgerRequiredByAgencyPolicy
                }
                isRentalLedgerRequiredForAddressByPolicy={
                  isRentalLedgerRequiredForAddressByPolicy
                }
                isLoading={isDocumentLoading}
              />
            )}

          {/* Reference Information - conditionally rendered */}
          {!hideRefereeDetails && (
            <RefereeSection
              values={values}
              isEditMode={isEditMode}
              isQLDForm22={isQLDForm22}
            />
          )}

          {/* Form Actions - conditionally rendered */}
          {!hideSubmitButton && (
            <FormActions
              isLoading={formIsLoading}
              canSubmit={canSubmit}
              onSubmitForm={handleSubmitForm}
            />
          )}
        </Flex>
      </Form>
    </Box>
  )
}
